import { useState, useEffect, useRef } from 'react';
import { NavLink, Link, useLocation } from 'react-router-dom';
import { fetchMenuItems, fetchServices } from '../lib/api';

export default function Navbar() {
  const [menuItems, setMenuItems] = useState([]);
  const [services, setServices] = useState([]);
  const [isOpen, setIsOpen] = useState(false);
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [isMobileYdelserOpen, setIsMobileYdelserOpen] = useState(false);
  const dropdownRef = useRef(null);
  const timeoutRef = useRef(null);
  const location = useLocation();

  useEffect(() => {
    async function loadData() {
      // Fetch menu items
      const items = await fetchMenuItems();
      if (items.length > 0) {
        setMenuItems(items);
      } else {
        // Fallback if API fails
        setMenuItems([
          { label: 'Forside', href: '/' },
          { label: 'Ydelser', href: '/ydelser' },
          { label: 'Om os', href: '/om-os' },
          { label: 'Projekter', href: '/projekter' },
          { label: 'Kontakt', href: '/kontakt' }
        ]);
      }

      // Fetch services for the dropdown
      const servicesData = await fetchServices();
      setServices(servicesData);
    }
    loadData();
  }, []);

  // Close menus on route change
  useEffect(() => {
    setIsOpen(false);
    setIsDropdownOpen(false);
    setIsMobileYdelserOpen(false);
    if (timeoutRef.current) clearTimeout(timeoutRef.current);
  }, [location]);

  // Close desktop dropdown when clicking outside
  useEffect(() => {
    function handleClickOutside(event) {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsDropdownOpen(false);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      if (timeoutRef.current) clearTimeout(timeoutRef.current);
    };
  }, []);

  const handleMouseEnter = () => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }
    setIsDropdownOpen(true);
  };

  const handleMouseLeave = () => {
    timeoutRef.current = setTimeout(() => {
      setIsDropdownOpen(false);
    }, 250); // 250ms delay to prevent accidental closing
  };

  return (
    <header className="sticky top-0 z-50 w-full border-b border-slate-200 bg-white">
      <div className="mx-auto flex max-w-7xl py-4 min-h-24 items-center justify-between px-6 lg:px-8">
        {/* Logo */}
        <Link to="/" className="flex items-center gap-3 group">
          <img
            src="https://assets.auxodev.dk/cdn-cgi/image/width=320,quality=90,format=auto/assets/4ff98460-cf40-4566-82c8-07bb5181a02e/logo_1.png"
            alt="Røjsmose Isolering ApS"
            className="h-20 w-auto object-contain transition-transform duration-300 group-hover:scale-102"
          />
        </Link>

        {/* Desktop Navigation (Visible on screens >= 1200px) */}
        <nav className="hidden min-[1200px]:flex items-center gap-8">
          {menuItems.map((item) => {
            // Check if this is the "Ydelser" menu item to render a dropdown
            if (item.href === '/ydelser') {
              const isYdelserActive = location.pathname.startsWith('/ydelser');
              return (
                <div
                  key={item.href}
                  ref={dropdownRef}
                  className="relative py-2"
                  onMouseEnter={handleMouseEnter}
                  onMouseLeave={handleMouseLeave}
                >
                  <button
                    type="button"
                    onClick={() => setIsDropdownOpen(!isDropdownOpen)}
                    className={`flex items-center gap-1.5 text-sm font-semibold tracking-wider uppercase transition-colors duration-200 border-b-2 cursor-pointer ${
                      isYdelserActive
                        ? 'text-black border-black'
                        : 'text-black/70 border-transparent hover:text-black hover:border-black'
                    }`}
                    aria-expanded={isDropdownOpen}
                    aria-haspopup="true"
                  >
                    {item.label}
                    <i className={`fa-solid fa-chevron-down text-xs transition-transform duration-200 ${isDropdownOpen ? 'rotate-180' : ''}`} aria-hidden="true" />
                  </button>

                  {/* Dropdown Menu */}
                  {isDropdownOpen && (
                    <div className="absolute left-1/2 -translate-x-1/2 top-full mt-1 w-64 border border-slate-100 bg-white p-2 shadow-xl animate-in fade-in slide-in-from-top-2 duration-200">
                      <div className="space-y-0.5">
                        {services.map((service) => (
                          <Link
                            key={service.id}
                            to={`/ydelser/${service.service_slug}`}
                            className="block px-3 py-2 text-sm font-semibold text-slate-700 hover:bg-slate-100 hover:text-black transition-colors"
                          >
                            {service.title}
                          </Link>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              );
            }

            return (
              <NavLink
                key={item.href}
                to={item.href}
                className={({ isActive }) =>
                  `text-sm font-semibold tracking-wider uppercase transition-colors duration-200 py-2 border-b-2 ${
                    isActive
                      ? 'text-black border-black'
                      : 'text-black/70 border-transparent hover:text-black hover:border-black'
                  }`
                }
              >
                {item.label}
              </NavLink>
            );
          })}
          <Link
            to="/kontakt"
            className="ml-4 inline-flex items-center justify-center rounded-full bg-brand-orange px-5 py-2.5 text-sm font-bold text-white shadow-sm transition-all duration-200 hover:bg-brand-orange-hover hover:scale-[1.02] active:scale-[0.98]"
          >
            Få et tilbud
          </Link>
        </nav>

        {/* Hamburger Button (Visible on screens < 1200px) */}
        <div className="flex min-[1200px]:hidden">
          <button
            type="button"
            onClick={() => setIsOpen(!isOpen)}
            className="inline-flex items-center justify-center p-2 text-brand-navy transition-colors"
            aria-expanded={isOpen}
            aria-label="Hovedmenu"
          >
            <span className="sr-only">Åbn hovedmenu</span>
            <i className={`fa-light ${isOpen ? 'fa-xmark text-2xl' : 'fa-bars text-2xl'}`} aria-hidden="true" />
          </button>
        </div>
      </div>

      {/* Mobile Menu (Visible on screens < 1200px when open) */}
      {isOpen && (
        <div className="min-[1200px]:hidden border-t border-slate-100 bg-white shadow-lg animate-in slide-in-from-top duration-200 max-h-[calc(100vh-5rem)] overflow-y-auto">
          <div className="space-y-1 px-6 py-4">
            {menuItems.map((item) => {
              if (item.href === '/ydelser') {
                const isYdelserActive = location.pathname.startsWith('/ydelser');
                return (
                  <div key={item.href} className="space-y-1">
                    <button
                      type="button"
                      onClick={() => setIsMobileYdelserOpen(!isMobileYdelserOpen)}
                      className={`flex w-full items-center justify-between rounded-md px-4 py-3 text-base font-semibold uppercase tracking-wider transition-colors ${
                        isYdelserActive
                          ? 'bg-slate-100 text-black'
                          : 'text-black/70 hover:bg-slate-50 hover:text-black'
                      }`}
                    >
                      <span>{item.label}</span>
                      <i className={`fa-solid fa-chevron-down text-sm transition-transform duration-200 ${isMobileYdelserOpen ? 'rotate-180' : ''}`} aria-hidden="true" />
                    </button>

                    {/* Mobile Dropdown Accordion */}
                    {isMobileYdelserOpen && (
                      <div className="pl-4 space-y-1 border-l-2 border-slate-100 ml-4 py-1 animate-in slide-in-from-top-1 duration-150">
                        {services.map((service) => (
                          <Link
                            key={service.id}
                            to={`/ydelser/${service.service_slug}`}
                            onClick={() => setIsOpen(false)}
                            className="block px-4 py-2 text-sm font-semibold text-slate-600 hover:bg-slate-50 hover:text-black transition-colors"
                          >
                            {service.title}
                          </Link>
                        ))}
                      </div>
                    )}
                  </div>
                );
              }

              return (
                <NavLink
                  key={item.href}
                  to={item.href}
                  onClick={() => setIsOpen(false)}
                  className={({ isActive }) =>
                    `block rounded-md px-4 py-3 text-base font-semibold uppercase tracking-wider transition-colors ${
                      isActive
                        ? 'bg-slate-100 text-black'
                        : 'text-black/70 hover:bg-slate-50 hover:text-black'
                    }`
                  }
                >
                  {item.label}
                </NavLink>
              );
            })}
            <div className="pt-4 border-t border-slate-100 mt-4">
              <Link
                to="/kontakt"
                onClick={() => setIsOpen(false)}
                className="flex w-full items-center justify-center rounded-full bg-brand-orange px-4 py-3 text-base font-bold text-white shadow-sm transition-all hover:bg-brand-orange-hover"
              >
                Få et tilbud
              </Link>
            </div>
          </div>
        </div>
      )}
    </header>
  );
}
